import { motion } from "framer-motion";
import { Reveal, PageHero, SectionHeading, Eyebrow, CTABand } from "@/components/Common";
import { COMPANY, IMAGES } from "@/data/site";
import { ShieldCheck, Users, ClipboardCheck, Handshake } from "lucide-react";

const CHAPTERS = [
  {
    num: "01",
    title: "Our Journey",
    text: `${COMPANY.name} was incorporated on ${COMPANY.incorporated} as a Non-Government Private Limited Company in the Business Services sector. From our head office at Sector 17-B, Chandigarh, we have grown steadily — one deployment, one site and one client relationship at a time.`,
  },
  {
    num: "02",
    title: "What We Do",
    text: "We provide manpower outsourcing, security manpower, facility management, sanitation and cleaning, warehousing management, event security and specialized workforce solutions for government institutions, corporates, industries and commercial establishments.",
  },
  {
    num: "03",
    title: "How We Work",
    text: "Every engagement begins with understanding the client's operational requirement. We then recruit, deploy and supervise personnel against a defined duty structure — with attendance monitoring, compliance management and a single point of accountability.",
  },
];

const VALUES = [
  { icon: ShieldCheck, title: "Reliability", desc: "Personnel you can depend on, shift after shift." },
  { icon: Users, title: "Professionalism", desc: "Uniformed, trained and supervised workforce." },
  { icon: ClipboardCheck, title: "Compliance", desc: "Statutory and wage compliance managed by us." },
  { icon: Handshake, title: "Accountability", desc: "One partner, one escalation point, full ownership." },
];

const About = () => (
  <>
    <PageHero
      crumb="Home — About Us"
      title="About Daksh"
      subtitle="An experienced manpower and workforce solutions partner with more than two decades of operational experience."
    />

    <section className="py-16 sm:py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-14 items-start">
        <div className="lg:col-span-3">
          <Reveal>
            <Eyebrow>Company Profile</Eyebrow>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-navy mt-4 leading-tight">
              Established. Structured. Dependable.
            </h2>
          </Reveal>
          <div className="mt-10 space-y-10">
            {CHAPTERS.map((ch, i) => (
              <Reveal key={ch.num} delay={i * 0.08}>
                <div className="flex gap-6 sm:gap-8 group" data-testid={`about-chapter-${ch.num}`}>
                  <span className="font-heading text-4xl sm:text-5xl font-extrabold text-slate-200 group-hover:text-gold transition-colors leading-none shrink-0">{ch.num}</span>
                  <div>
                    <h3 className="font-heading text-xl font-semibold text-navy">{ch.title}</h3>
                    <p className="mt-3 text-slate-600 leading-relaxed">{ch.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal delay={0.15} className="lg:col-span-2 lg:sticky lg:top-32">
          <div className="bg-navy text-white p-8 sm:p-10 relative overflow-hidden hero-grain" data-testid="company-facts-card">
            <div className="absolute -right-16 -top-16 w-48 h-48 rounded-full bg-gold/10 blur-2xl" />
            <h3 className="font-heading text-lg font-bold tracking-wide text-gold uppercase text-sm tracking-[0.2em]">Company Facts</h3>
            <dl className="mt-6 space-y-5 relative">
              {[
                ["Company", COMPANY.name],
                ["Company Type", "Non-Government Private Limited Company"],
                ["Incorporated", COMPANY.incorporated],
                ["Experience", "23+ years"],
                ["Managing Director", COMPANY.md],
                ["Head Office", COMPANY.address],
              ].map(([k, v]) => (
                <div key={k} className="border-b border-white/10 pb-4">
                  <dt className="text-[11px] font-semibold tracking-[0.18em] uppercase text-slate-400">{k}</dt>
                  <dd className="mt-1 text-sm text-white leading-relaxed">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="py-16 sm:py-20 bg-[#FAFAFC]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <div className="relative">
            <div className="absolute -bottom-5 -right-5 w-full h-full border-2 border-gold pointer-events-none" />
            <img src={IMAGES.facility} alt="Professionally managed facility" className="relative w-full h-[380px] sm:h-[460px] object-cover" />
          </div>
        </Reveal>
        <div>
          <SectionHeading
            eyebrow="Leadership"
            title="A Note from Our Managing Director"
          />
          <Reveal delay={0.1}>
            <blockquote className="border-l-4 border-gold pl-6 text-lg text-slate-700 leading-relaxed italic" data-testid="md-note">
              "Since 2003, our commitment has remained unchanged — provide organizations with manpower they can rely on, managed with professionalism and integrity. We do not promise what we cannot deliver; we deliver what we promise."
            </blockquote>
            <p className="mt-6 font-heading font-bold text-navy">{COMPANY.md}</p>
            <p className="text-sm text-slate-500">Managing Director, {COMPANY.shortName} Detective and Security Services Pvt. Ltd.</p>
          </Reveal>
        </div>
      </div>
    </section>

    <section className="py-16 sm:py-20 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading center eyebrow="Our Values" title="The Principles Behind Every Deployment" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-7">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="border border-slate-200 p-8 text-center h-full transition-all hover:border-gold hover:-translate-y-1 hover:shadow-lg hover:shadow-navy/5" data-testid={`value-${v.title.toLowerCase()}`}>
                <div className="w-12 h-12 bg-gold-soft flex items-center justify-center mx-auto">
                  <v.icon size={22} className="text-gold-hover" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-navy mt-5">{v.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <CTABand />
  </>
);

export default About;
